import { merge } from "theme-ui"
import originalTheme from "@lekoarts/gatsby-theme-minimal-blog/src/gatsby-plugin-theme-ui/index"
import { block } from "sharp"

const theme = merge(originalTheme, {
  config: {
    initialColorModeName: "light",
    useColorSchemeMediaQuery: false,
  },
  colors: {
    primary: "#1DA1F2",    // メイン：旧Twitterのライトブルー（爽やか＆信頼感）
    secondary: "#FF69B4",  // アクセント：ポップなピンク（親しみやすく元気）
    heading: "#15202B",    // 濃いネイビー（Twitterの見出し色風）
    background: "#ffffff", // 白背景で清潔感
    text: "#333333",       // 標準テキスト（可読性重視）
    muted: "#94A3B8",      // 補足・薄いグレー（落ち着いたトーン）
    toggleIcon: "#dfdfff", // トグルはブランドブルーで
    quoteBg: "rgba(29, 161, 242, 0.05)", // 引用ブロック用の薄い青背景

    modes: {
      dark: {
        primary: "#1DA1F2",    // ブランドブルーは継続
        secondary: "#FF69B4",  // ポップピンクで遊び心
        heading: "#F8FAFC",    // ダークでも明るい見出し
        background: "#15202B", // Twitter風の深いネイビーバック
        text: "#E2E8F0",       // 明るいグレーで可読性アップ
        muted: "#94A3B8",      // 共通
        toggleIcon: "#ffffff", // 暗背景で目立つ白アイコン
        quoteBg: "rgba(29, 161, 242, 0.1)", // ダークモード用の引用ブロック背景
      },
    },
  },
  fonts: {
    body: `"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
    heading: `"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
  },
  styles: {
    root: {
      color: `text`,
      backgroundColor: `background`,
      // CSS変数を直接定義（より確実な初期化）
      '--theme-ui-colors-toggleIcon': '#dfdfff',
      '--theme-ui-colors-primary': '#1DA1F2',
      '--theme-ui-colors-secondary': '#FF69B4',
      '--theme-ui-colors-background': '#ffffff',
      '--theme-ui-colors-text': '#333333',
      '--theme-ui-colors-muted': '#94A3B8',
      '--theme-ui-colors-heading': '#15202B',

      // ダークモード用のCSS変数
      '&.theme-ui-dark': {
        '--theme-ui-colors-toggleIcon': '#ffffff',
        '--theme-ui-colors-primary': '#1DA1F2',
        '--theme-ui-colors-secondary': '#FF69B4',
        '--theme-ui-colors-background': '#15202B',
        '--theme-ui-colors-text': '#E2E8F0',
        '--theme-ui-colors-muted': '#94A3B8',
        '--theme-ui-colors-heading': '#F8FAFC',
      },

      // メディアクエリでダークモードを検出した場合の初期化
      '@media (prefers-color-scheme: dark)': {
        '&:not(.theme-ui-light)': {
          '--theme-ui-colors-toggleIcon': '#ffffff',
          '--theme-ui-colors-background': '#15202B',
          '--theme-ui-colors-text': '#E2E8F0',
          '--theme-ui-colors-heading': '#F8FAFC',
        }
      },
    },
    h1: {
      fontSize: [3, 4, 5],
      mt: 4,
      mb: 2,
    },
    h2: {
      fontSize: [3, 4, 5],
      mt: 5,
      mb: 4,
      pb: 2,
      borderLeft: '6px solid',
      borderBottom: '2px solid',
      borderColor: 'primary',
      pl: 3,
      position: 'relative',
    },
    h3: {
      fontSize: [2, 3, 4],
      mt: 4,
      mb: 3,
      pb: 1,
      borderLeft: '3px solid',
      borderColor: 'secondary',
      pl: 3,
      position: 'relative',
    },
    h4: {
      fontSize: [1, 2, 3],
      mt: 3,
      mb: 2,
    },
    h5: {
      fontSize: [1, 1, 2],
      mt: 2,
      mb: 1,
    },
    h6: {
      fontSize: [1, 1, 1],
      mt: 2,
      mb: 1,
    },
    p: {
      fontSize: 1,
      lineHeight: 1.8,
      mb: 3,
      display: 'block',
      '& a': {
        color: 'primary',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    // リスト項目は本文サイズに揃える
    li: {
      fontSize: 1,
      lineHeight: 1.8,
    },
    // Minimal Blog が ul/ol 内の li にレスポンシブな fontSize を当てるため、ここで上書き
    ul: {
      li: {
        fontSize: 1,
        lineHeight: 1.8,
        letterSpacing: 'normal',
      },
    },
    ol: {
      li: {
        fontSize: 1,
        lineHeight: 1.8,
        letterSpacing: 'normal',
      },
    },
    blockquote: {
      borderLeft: '4px solid',
      borderColor: 'primary',
      px: 4,
      py: 3,
      my: 4,
      bg: 'quoteBg',
      fontSize: '90%',
      color: 'text',
      maxWidth: '100%',
      transition: 'all 0.3s ease-in-out', // CLS対策：スムーズな遷移
      willChange: 'transform, opacity', // パフォーマンス最適化
      '& a': {
        color: 'primary',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      // ツイートプレビュー用の特別なスタイル
      '&.tweet-preview': {
        borderColor: 'primary',
        backgroundColor: 'quoteBg',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },
})

export default theme
